import { type ReactNode } from 'react';

import { LWApiPublicAssetStatusT } from '@liquidweb-dev/club-sauce/public/asset/index.raw';
import DynamicBadge, {
	DynamicBadgeProps,
} from '@liquidweb-dev/undercurrent/DynamicBadge';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import CheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/Check';
import CircleXIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleX';
import ConstructionIcon from '@liquidweb-dev/undercurrent/icons/lucide/Construction';
import PowerIcon from '@liquidweb-dev/undercurrent/icons/lucide/Power';
import RotateCcwIcon from '@liquidweb-dev/undercurrent/icons/lucide/RotateCcw';
import ShredderIcon from '@liquidweb-dev/undercurrent/icons/lucide/Shredder';
import SparklesIcon from '@liquidweb-dev/undercurrent/icons/lucide/Sparkles';
import XIcon from '@liquidweb-dev/undercurrent/icons/lucide/X';
import NotificationIcon from 'components/standard/NotificationIcon';
import type { AssetDetails } from 'modules/queries/asset/useDetails';
import createServerStatus from 'utility/createServerStatus';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

export const getStatusProps = ({
	status,
	powerStatus,
	notificationPrimary,
	notificationSecondary,
}: {
	status: LWApiPublicAssetStatusT;
	powerStatus?: AssetDetails['powerStatus'];
	notificationPrimary?: string;
	notificationSecondary?: string;
}): {
	primaryInfo: string;
	secondaryInfo?: string;
	color: 'success' | 'highlight' | 'danger' | 'warning';
	icon: ReactNode;
} => {
	if (notificationPrimary) {
		return {
			primaryInfo: notificationPrimary,
			secondaryInfo:
				notificationSecondary === notificationPrimary
					? undefined
					: notificationSecondary,
			color: 'highlight',
			icon: (
				<NotificationIcon
					notification={notificationPrimary}
					sx={({ vars, size }) => ({
						color: vars.palette.uc.highlight.icon.main,
						width: size.xS,
						height: size.xS,
						flexShrink: 0,
					})}
				/>
			),
		};
	}

	const { level, label: primaryInfo } = createServerStatus(status, powerStatus);

	switch (level) {
		case 'on':
			return {
				primaryInfo,
				color: 'success',
				icon: (
					<CheckIcon
						sx={({ vars, size }) => ({
							color: vars.palette.uc.success.icon.strong,
							width: size.xS,
							height: size.xS,
							flexShrink: 0,
						})}
					/>
				),
			};

		case 'activity': {
			const color = 'highlight';

			switch (primaryInfo) {
				case 'Activating':
					return {
						primaryInfo,
						color,
						icon: (
							<ConstructionIcon
								sx={({ vars, size }) => ({
									color: vars.palette.uc.highlight.icon.main,
									width: size.xS,
									height: size.xS,
									flexShrink: 0,
								})}
							/>
						),
					};

				case 'Starting':
					return {
						primaryInfo,
						color,
						icon: (
							<LoaderCircleIcon
								sx={({ vars, size }) => ({
									color: vars.palette.uc.highlight.icon.main,
									width: size.xS,
									height: size.xS,
									flexShrink: 0,
								})}
							/>
						),
					};

				case 'Shutting down':
					return {
						primaryInfo,
						color,
						icon: (
							<PowerIcon
								sx={({ vars, size }) => ({
									color: vars.palette.uc.highlight.icon.main,
									width: size.xS,
									height: size.xS,
									flexShrink: 0,
								})}
							/>
						),
					};

				case 'Resetting':
				case 'Rebooting':
					return {
						primaryInfo,
						color,
						icon: (
							<RotateCcwIcon
								sx={({ vars, size }) => ({
									color: vars.palette.uc.highlight.icon.main,
									width: size.xS,
									height: size.xS,
									flexShrink: 0,
								})}
							/>
						),
					};

				case 'New':
					return {
						primaryInfo,
						color,
						icon: (
							<SparklesIcon
								sx={({ vars, size }) => ({
									color: vars.palette.uc.highlight.icon.main,
									width: size.xS,
									height: size.xS,
									flexShrink: 0,
								})}
							/>
						),
					};

				case 'Terminating':
					return {
						primaryInfo,
						color,
						icon: (
							<ShredderIcon
								sx={({ vars, size }) => ({
									color: vars.palette.uc.highlight.icon.main,
									width: size.xS,
									height: size.xS,
									flexShrink: 0,
								})}
							/>
						),
					};

				default:
					return {
						primaryInfo,
						color,
						icon: (
							<LoaderCircleIcon
								sx={({ vars, size }) => ({
									color: vars.palette.uc.highlight.icon.main,
									width: size.xS,
									height: size.xS,
									flexShrink: 0,
								})}
							/>
						),
					};
			}
		}

		case 'off':
			return {
				primaryInfo,
				color: 'warning',
				icon: (
					<CircleXIcon
						sx={({ vars, size }) => ({
							color: vars.palette.uc.warning.icon.strong,
							width: size.xS,
							height: size.xS,
							flexShrink: 0,
						})}
					/>
				),
			};

		default:
			return {
				primaryInfo,
				color: 'danger',
				icon: (
					<XIcon
						sx={({ vars, size }) => ({
							color: vars.palette.uc.danger.icon.main,
							width: size.xS,
							height: size.xS,
							flexShrink: 0,
						})}
					/>
				),
			};
	}
};

export interface ServerStatusBadgeFlatProps extends Partial<DynamicBadgeProps> {
	status: LWApiPublicAssetStatusT;
	powerStatus?: AssetDetails['powerStatus'];
	notificationPrimary?: string;
	notificationSecondary?: string;
}

const ServerStatusBadgeFlat = ({
	status,
	powerStatus,
	notificationPrimary,
	notificationSecondary,
	...rest
}: ServerStatusBadgeFlatProps) => {
	const { primaryInfo, color, icon, secondaryInfo } = getStatusProps({
		status,
		powerStatus,
		notificationPrimary,
		notificationSecondary,
	});

	return (
		<DynamicBadge
			{...rest}
			showShadow={false}
			alwaysExpanded
			icon={icon}
			color={color}
			primaryInfo={primaryInfo}
			secondaryInfo={secondaryInfo}
			sx={combineSx(rest.sx || {}, { cursor: 'inherit' })}
		/>
	);
};

export default ServerStatusBadgeFlat;
